import type { TFunction } from "i18next";
import { z } from "zod";

import { isDateInFuture, validateDateString } from "./utils";

export const getAmlFormScheme = (t: TFunction) =>
    z
        .object({
            firstName: z.string().min(1, { message: t("verification.AML.personalInfo.inputs.firstName.error") }),
            lastName: z.string().min(1, { message: t("verification.AML.personalInfo.inputs.lastName.error") }),
            residentialAddress: z.string().min(1, { message: t("verification.AML.personalInfo.inputs.address.error") }),
            countryAddress: z.string().min(1, { message: t("verification.AML.personalInfo.inputs.addressCountry.error") }),
            cityAddress: z.string().min(1, { message: t("verification.AML.personalInfo.inputs.addressCity.error") }),
            postalCode: z.string().min(1, { message: t("verification.AML.personalInfo.inputs.addressPostalCode.error") }),
            dateOfBirth: z.string().refine((value) => validateDateString(value), {
                message: t("verification.AML.personalInfo.inputs.dateOfBirth.error"),
            }),
            socialSecurityNumber: z.string().optional(),
            idOrPassportNumber: z.string().min(1, { message: t("verification.AML.personalInfo.inputs.idPassportNumber.error") }),
            countryOfOrigin: z.string().min(1, { message: t("verification.AML.personalInfo.inputs.countryOfOrigin.error") }),

            citizenship: z.string().min(1, { message: t("verification.AML.personalInfo.inputs.citizenship.error") }),
            documentValidUntil: z
                .string()
                .refine((value) => validateDateString(value), {
                    message: t("verification.AML.personalInfo.inputs.documentValidUntil.error"),
                })
                .refine((value) => isDateInFuture(value), {
                    message: t("verification.AML.personalInfo.inputs.documentValidUntil.errorExpired"),
                }),
            documentIssuedBy: z.string().min(1, { message: t("verification.AML.personalInfo.inputs.documentIssuedBy.error") }),
            purposeOfContract: z.string().min(1, { message: t("verification.AML.additionalInfo.inputs.purposeOfContract.error") }),
            expectedTradesVolume: z.string({
                required_error: t("verification.AML.additionalInfo.inputs.expectedTradesVolume.error"),
            }),
            originOfFunds: z
                .object({
                    alimonyBenefitsPension: z.boolean().optional(),
                    salary: z.boolean().optional(),
                    business: z.boolean().optional(),
                    capital: z.boolean().optional(),
                    otherChecked: z.boolean().optional(),
                    otherValue: z.string().optional(),
                })
                .refine((data) => data.alimonyBenefitsPension || data.business || data.capital || data.salary || data.otherChecked, {
                    message: t("verification.AML.additionalInfo.inputs.originOfFunds.error"),
                })
                .refine(
                    (data) => {
                        if (data.otherChecked) {
                            return typeof data.otherValue === "string" && data.otherValue.trim() !== "";
                        }
                        return true;
                    },
                    {
                        message: t("verification.AML.additionalInfo.inputs.originOfFunds.option.other.error"),
                    },
                ),
            noCriminalActivity: z.string({ required_error: t("verification.AML.additionalInfo.inputs.criminalActivity.error") }),
            countriesWithSanctions: z.string({
                required_error: t("verification.AML.additionalInfo.inputs.countriesWithSanctions.error"),
            }),
            politicallyExposed: z
                .object({
                    isChecked: z.string({ required_error: t("verification.AML.additionalInfo.inputs.politicallyExposed.error") }),
                    sourceOfWealthSalary: z.boolean().optional(),
                    sourceOfWealthCapital: z.boolean().optional(),
                    sourceOfWealthBusiness: z.boolean().optional(),
                    sourceOfWealthInheritance: z.boolean().optional(),
                    sourceOfWealthOtherChecked: z.boolean().optional(),
                    sourceOfWealthOtherValue: z.string().optional(),
                })
                .refine(
                    (data) => {
                        if (data.isChecked === "yes") {
                            return (
                                data.sourceOfWealthSalary ||
                                data.sourceOfWealthCapital ||
                                data.sourceOfWealthBusiness ||
                                data.sourceOfWealthInheritance ||
                                data.sourceOfWealthOtherChecked
                            );
                        }
                        return true;
                    },
                    {
                        message: t("verification.AML.additionalInfo.inputs.peOriginOfFunds.error"),
                    },
                )
                .refine(
                    (data) => {
                        if (data.sourceOfWealthOtherChecked) {
                            return typeof data.sourceOfWealthOtherValue === "string" && data.sourceOfWealthOtherValue.trim() !== "";
                        }
                        return true;
                    },
                    {
                        message: t("verification.AML.additionalInfo.inputs.peOriginOfFunds.option.other.error"),
                    },
                ),
            finalCheck: z.boolean().refine((val) => val, {
                message: t("verification.AML.finalCheck.error"),
            }),
        })
        .superRefine((data, ctx) => {
            const czOrSk = ["CZ", "SK", "CZE", "SVK"];
            const isCzOrSk = czOrSk.includes(data.citizenship?.toUpperCase());
            const ssn = data.socialSecurityNumber?.trim();
            const ssnFormat = /^\d{6}[/ ]?\d{3,4}$/;

            if (isCzOrSk && !ssn) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: t("verification.AML.personalInfo.inputs.socialSecurityNumber.error"),
                    path: ["socialSecurityNumber"],
                });
            } else if (ssn && !ssnFormat.test(ssn)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: t("verification.AML.personalInfo.inputs.socialSecurityNumber.errorFormat"),
                    path: ["socialSecurityNumber"],
                });
            }
        });
